import { formatNumberToPercentage, formatStringToNumber } from '@go-shared/lib/utils';
import React, { useEffect, useRef, useState } from 'react';
import BubbleInput, { BubbleInputProps } from './BubbleInput';

export type BubblePercentageProps = Omit<BubbleInputProps, 'type' | 'value' | 'onValue'> & {
  value: number | undefined;
  onValue?: (value: number | undefined) => void;
};

export default function BubblePercentage(props: BubblePercentageProps) {
  const {
    value: numberValue,
    onValue: onNumberValue,
    onBlur: propsOnBlur,
    onChange: propsOnChange,
    ...rest
  } = props;

  const internalChangeRef = useRef(false);
  const [textValue, setTextValue] = useState(() => formatNumberToPercentage(numberValue));

  useEffect(() => {
    if (internalChangeRef.current) {
      internalChangeRef.current = false;
      return;
    }
    setTextValue(formatNumberToPercentage(numberValue));
  }, [numberValue]);

  function clamp(val: number | undefined) {
    if (val === undefined) return undefined;
    return Math.min(100, Math.max(0, val));
  }

  function handleOnBlur(evt: React.FocusEvent<HTMLInputElement>) {
    // Normalize, clamp, and format on blur
    const val = clamp(formatStringToNumber(textValue));
    setTextValue(formatNumberToPercentage(val));
    onNumberValue?.(val);
    propsOnBlur?.(evt);
  }

  function handleOnChange(evt: React.ChangeEvent<HTMLInputElement>) {
    // Sanitize the value before passing to Formik's handleChange so it receives the
    // parsed number string (e.g. "105") rather than the raw "105%" string.
    const val = clamp(formatStringToNumber(evt.target.value));
    evt.target.value = val !== undefined ? String(val) : '';
    propsOnChange?.(evt);
  }

  function handleOnValue(value: string) {
    internalChangeRef.current = true;
    setTextValue(value);
    const val = formatStringToNumber(value);
    onNumberValue?.(val);
  }

  return (
    <BubbleInput
      type="text"
      value={textValue}
      onValue={handleOnValue}
      onBlur={handleOnBlur}
      onChange={handleOnChange}
      {...rest}
    />
  );
}
